import { linearOnUnitInterval, Vector2 } from '@cgs/wge';
import { EntitiesEngine } from '../entities_engine/entities_engine';
import { Entity } from '../entities_engine/entity';
import { BrakingCalculationInfo } from '../entity_components/braking_calculation_info';
import { ComponentNames } from '../entity_components/component_names';
import { SpeedInterpolation } from '../entity_components/speed_interpolation';
import { EntityCacheHolder } from '../game_components/entity_cache_holder';
import { IconEnumerator } from '../icon_enumerator';
import { InternalReelsConfig } from '../internal_reels_config';
import { BeforeBrakingSystem } from './before_braking_system';

export class BeforeBrakingSystemV2 extends BeforeBrakingSystem {
  constructor(
    engine: EntitiesEngine,
    entityCacheHolder: EntityCacheHolder,
    config: InternalReelsConfig,
    enumerator: IconEnumerator
  ) {
    super(engine, entityCacheHolder, config, enumerator);
  }

  CalculateSecondPrecisionTime(breakInfo: BrakingCalculationInfo, reel: number): void {
    const y = breakInfo.position.y;

    const skip =
      y -
      Math.floor(y / this.config.symbolSizeByReel[reel].y) * this.config.symbolSizeByReel[reel].y;
    const destination = new Vector2(
      0.0,
      this.config.slotSizeByReel[reel].y - Math.sign(breakInfo.speed.y) * skip
    );
    const linearTime = destination.y / Math.abs(breakInfo.speed.y);
    this.integratedTime[reel] = linearTime / this.integratedDest[reel];
    this.integratedTime[reel] =
      linearTime / this.interpolateSpeedFunc(reel, 1 / this.integratedTime[reel]);
  }

  CalculateSecondPrecisionTimeSingleEntity(
    breakInfo: BrakingCalculationInfo,
    reel: number,
    line: number
  ): void {
    const curLine = Math.floor(breakInfo.position.y) / this.config.symbolSizeByReel[reel].y;
    const diff = line - curLine + 1;

    const destination = new Vector2(
      0.0,
      this.config.symbolSizeByReel[reel].y * 3 -
        Math.sign(breakInfo.speed.y) +
        diff * this.config.symbolSizeByReel[reel].y
    );

    const linearTime = destination.y / Math.abs(breakInfo.speed.y);
    const integratedTime = linearTime / this.integratedDest[reel];

    this.integratedTime[reel] = linearTime / this.interpolateSpeedFunc(reel, 1 / integratedTime);
  }

  processSlotSpinningEntity(entity: Entity): void {
    const breakInfo = entity.get(this.brakingInfoIndex) as BrakingCalculationInfo;
    const reel = entity.get(this.reelIndex) as number;
    const offset = this.config.reelsOffset[reel] as Vector2;
    breakInfo.position.subVector(new Vector2(0.0, offset.y)); // offset for X we apply in ReelsEngine CreateEntites()

    const posInReel = Math.floor(breakInfo.position.y / this.config.symbolSizeByReel[reel].y);

    if (Math.abs(this.integratedTime[reel]) <= 0.0000001)
      this.CalculateSecondPrecisionTime(breakInfo, reel);
    const interpolate = new SpeedInterpolation()
      .withParameters(
        this.integratedTime[reel],
        new Vector2(0.0, breakInfo.speed.y),
        Vector2.Zero,
        Vector2.lerp
      )
      .withTimeFunction(this.config.getStopEasing(reel));
    const enumerationId = entity.get(this.enumerationIndex) as number;
    if (posInReel >= 0 && posInReel < this.config.lineCount - this.config.additionalLines) {
      const speedY = Math.sign(breakInfo.speed.y);
      const finalEnumerationId = enumerationId + this.config.lineCount * speedY;
      this.enumerator.setMappedWinIndex(reel, posInReel, finalEnumerationId);
    }
    this.entityCacheHolder.replaceEntities(reel, posInReel, [entity]);
    entity.addComponent(ComponentNames.PositionInReel, posInReel);
    entity.addComponent(ComponentNames.CalculationReadyFlag, true);
    entity.addComponent(ComponentNames.BrakingInterpolate, interpolate);
    entity.addComponent(
      ComponentNames.FinalPosition,
      new Vector2(breakInfo.position.x, posInReel * this.config.symbolSizeByReel[reel].y + offset.y)
    );

    entity.addComponent(
      ComponentNames.FinalEnumerationIndex,
      enumerationId + this.config.lineCount * Math.sign(breakInfo.speed.y)
    );
    entity.addComponent(ComponentNames.CheckIconIndex, true);
    entity.removeComponent(ComponentNames.BrakingCalculationInfo);
  }

  processSingleSpinningEntity(entity: Entity): void {
    const breakInfo = entity.get<BrakingCalculationInfo>(this.brakingInfoIndex);
    const reel = entity.get<number>(this.reelIndex);
    const line = entity.get<number>(this.lineIndex);
    const offset = this.config.reelsOffset[reel];
    const totalLines = 1 + this.config.additionalLines;

    breakInfo.position.subVector(new Vector2(0.0, offset.y));

    const currentPosition = Math.floor(breakInfo.position.y / this.config.symbolSizeByReel[reel].y);
    const diff = line - currentPosition + 1;

    if (Math.abs(this.integratedTime[reel]) <= 0.0000001) {
      this.CalculateSecondPrecisionTimeSingleEntity(breakInfo, reel, line);
    }

    const speedY = Math.sign(breakInfo.speed.y);
    const direction = Math.sign(diff);

    const interpolate = new SpeedInterpolation()
      .withParameters(
        this.integratedTime[reel],
        new Vector2(0.0, breakInfo.speed.y),
        Vector2.Zero,
        Vector2.lerp
      )
      //TODO: think how to get throw config
      .withTimeFunction(linearOnUnitInterval);

    const enumerationId = entity.get<number>(this.enumerationIndex);

    const finalEnumerationId = enumerationId + totalLines * speedY + totalLines * direction;
    this.enumerator.setMappedWinIndex(reel, line, finalEnumerationId);

    this.entityCacheHolder.replaceEntities(reel, line, [entity]);

    entity.addComponent(ComponentNames.PositionInReel, line);
    entity.addComponent(ComponentNames.CalculationReadyFlag, true);
    entity.addComponent(ComponentNames.BrakingInterpolate, interpolate);

    const finalPosition = new Vector2(
      breakInfo.position.x,
      line * this.config.symbolSizeByReel[reel].y + offset.y
    );

    entity.addComponent(ComponentNames.FinalPosition, finalPosition);

    entity.addComponent(
      ComponentNames.FinalEnumerationIndex,
      enumerationId + totalLines * speedY + totalLines * direction
    );
    entity.addComponent(ComponentNames.CheckIconIndex, true);
    entity.removeComponent(ComponentNames.BrakingCalculationInfo);
  }
}
